import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, MapPin, LocateFixed, CalendarDays, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { trackClick, DIR_SPORTS } from "@/lib/asv";
import { VenueCard, EventCard, GameCard, Section, Empty, Tag } from "@/components/asv/Cards";
import { Rail, RailItem } from "@/components/asv/Rail";
import { browserLocation, geocode, useMapsEnabled, usePlaceLabel } from "@/lib/geo";

const HERO = "https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=1800&q=70";

function Slider({ slides, t }) {
  const [i, setI] = useState(0);
  const list = slides.length ? slides : [{ id: "default", title: t?.hero_title || "Every court. Every game. One place.", subtitle: t?.hero_subtitle || "Book venues, join events and find players near you.", image: HERO }];
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % list.length), 6000); return () => clearInterval(t); }, [list.length]);
  const s = list[i % list.length];
  const go = (d) => setI((x) => (x + d + list.length) % list.length);
  return (
    <div className="relative mt-4 h-[300px] overflow-hidden rounded-[2rem] sm:h-[440px]" data-testid="asv-home-slider">
      <a href={s.link || undefined} onClick={() => trackClick(s.campaign_id)} className="block h-full">
        <img key={s.image} src={s.image} alt={s.title || "slide"} className="asv-rise h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 max-w-3xl p-6 pb-14 text-white sm:p-10 sm:pb-16">
          {s.sponsored && <Tag>Sponsored</Tag>}
          {s.title && <h1 className="asv-display mt-3 text-4xl font-extrabold leading-[0.95] sm:text-5xl lg:text-6xl" data-testid="asv-slide-title">{s.title}</h1>}
          {s.subtitle && <p className="mt-3 text-sm text-white/80 sm:text-base">{s.subtitle}</p>}
        </div>
      </a>
      {list.length > 1 && <div className="absolute bottom-5 right-5 flex gap-2">
        <button onClick={() => go(-1)} className="grid h-10 w-10 place-items-center rounded-full bg-white/90 text-black" data-testid="slider-prev"><ChevronLeft className="h-4 w-4" /></button>
        <button onClick={() => go(1)} className="grid h-10 w-10 place-items-center rounded-full bg-white/90 text-black" data-testid="slider-next"><ChevronRight className="h-4 w-4" /></button></div>}
    </div>
  );
}

function SearchBar() {
  const nav = useNavigate();
  const [f, setF] = useState({ q: "", city: "", date: "", lat: "", lng: "" });
  const maps = useMapsEnabled();
  const here = usePlaceLabel(f.lat, f.lng, maps);
  const near = () => browserLocation().then((p) => setF((x) => ({ ...x, city: "", ...p }))).catch(() => {});
  const go = async (e) => {
    e.preventDefault();
    let x = f;
    if (maps && f.city && !f.lat) {
      const p = await geocode(f.city).catch(() => null);
      if (p) x = { ...f, city: "", lat: p.lat, lng: p.lng };
    }
    const p = new URLSearchParams(Object.entries({ ...x, sort: x.lat ? "nearest" : "" }).filter(([, v]) => v !== ""));
    nav(`/venues?${p}`);
  };
  return (
    <form onSubmit={go} className="asv-card relative z-10 mx-auto -mt-10 grid max-w-5xl gap-2 p-3 shadow-2xl sm:grid-cols-[1.4fr_1fr_0.9fr_auto]" data-testid="asv-search-bar">
      <label className="flex items-center gap-2 rounded-2xl px-3"><Search className="asv-muted h-4 w-4 shrink-0" /><input list="asv-sports" className="h-12 w-full bg-transparent text-sm outline-none" placeholder="Sport, venue or event" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="search-what" /></label>
      <datalist id="asv-sports">{DIR_SPORTS.map((s) => <option key={s} value={s} />)}</datalist>
      <label className="flex items-center gap-2 rounded-2xl px-3 sm:border-l asv-line"><MapPin className="asv-muted h-4 w-4 shrink-0" /><input className="h-12 w-full bg-transparent text-sm outline-none" placeholder={f.lat ? (here ? `Near ${here}` : "Near me") : "City or area"} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value, lat: "", lng: "" })} data-testid="search-where" />
        <button type="button" onClick={near} className="asv-accent" title="Near me" data-testid="search-near-me"><LocateFixed className="h-4 w-4" /></button></label>
      <label className="flex items-center gap-2 rounded-2xl px-3 sm:border-l asv-line"><CalendarDays className="asv-muted h-4 w-4 shrink-0" /><input type="date" className="h-12 w-full bg-transparent text-sm outline-none" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="search-when" /></label>
      <button className="asv-btn h-12 !px-7" data-testid="search-submit">Search</button>
    </form>
  );
}

const Grid = ({ items, Card, empty, testid }) => items?.length
  ? <Rail testid={testid}>{items.map((x) => <RailItem key={x.key || x.id}><Card x={x} /></RailItem>)}</Rail>
  : <Empty text={empty} />;

export default function AsvHome() {
  const [d, setD] = useState(null);
  const [t, setT] = useState({});
  useEffect(() => { api.get("/asv/home").then((r) => setD(r.data)).catch(() => setD({})); api.get("/asv/home-content").then((r) => setT(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="asv-home">
      <Slider slides={d?.slides || []} t={t} />
      <SearchBar />
      {d?.featured?.length > 0 && <Section title={t.featured_title || "Featured venues"} to="/venues" testid="home-featured"><Grid items={d.featured} Card={VenueCard} testid="rail-featured" /></Section>}
      <Section title={t.trending_title || "Trending venues"} to="/venues?sort=trending" testid="home-trending"><Grid items={d?.trending} Card={VenueCard} testid="rail-trending" empty="Venues will appear here as they join." /></Section>
      <Section title={t.events_title || "Upcoming events"} to="/events?sort=soonest" testid="home-events"><Grid items={d?.upcoming} Card={EventCard} testid="rail-events" empty="No upcoming events yet." /></Section>
      <Section title={t.games_title || "Open games"} to="/games" testid="home-games"><Grid items={d?.games} Card={GameCard} testid="rail-games" empty="No open games yet. Be the first to host one." /></Section>
      <Section title={t.sports_title || "Browse by sport"} testid="home-sports">
        <Rail testid="rail-sports" chips>{DIR_SPORTS.map((s) => <Link key={s} to={`/venues?sport=${encodeURIComponent(s)}`} className="asv-chip shrink-0 snap-start whitespace-nowrap !px-4 !py-2 !text-sm" data-testid={`sport-${s}`}>{s}</Link>)}</Rail>
      </Section>
      <section className="asv-card mt-16 grid gap-6 overflow-hidden p-8 sm:p-12 md:grid-cols-2" data-testid="home-join-band">
        <div><p className="text-xs font-bold uppercase tracking-[0.25em] asv-accent">{t.join_kicker || "Join us"}</p><h2 className="asv-display mt-3 text-3xl font-extrabold sm:text-4xl" data-testid="home-join-title">{t.join_title || "Play more. Or fill your courts."}</h2></div>
        <div className="flex flex-col justify-center gap-3 sm:flex-row md:justify-end">
          <Link to="/join/user" className="asv-btn" data-testid="join-band-user">{t.join_player_cta || "I'm a player"} <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/join/vendor" className="asv-btn-ghost justify-center" data-testid="join-band-vendor">{t.join_vendor_cta || "I own a venue"}</Link>
        </div>
      </section>
    </div>
  );
}
